import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import "./bulkEditItem.css";

const currencySymbols = {
  GBP: "£",
  USD: "$",
  EUR: "€",
};

const buildVariantDisplayName = (parentName, variantName) => {
  const parent = (parentName || "").trim();
  const variant = (variantName || "").trim();
  if (parent && variant && parent.toLowerCase() !== variant.toLowerCase()) {
    return `${parent} (${variant})`;
  }
  return variant || parent || "Unknown Product";
};

const buildFormState = (itemData, settings) => ({
  menuItem_Name: itemData.menuItem_Name || "",
  menuItem_KitchenDisplayName: itemData.menuItem_KitchenDisplayName || "",
  menuItem_SalePrice:
    itemData.menuItem_SalePrice === 0 || itemData.menuItem_SalePrice
      ? String(itemData.menuItem_SalePrice)
      : "",
  menuItem_CostPrice:
    itemData.menuItem_CostPrice === 0 || itemData.menuItem_CostPrice
      ? String(itemData.menuItem_CostPrice)
      : "",
  menuItem_TaxPercentage: itemData.isTaxExempt
    ? ""
    : itemData.menuItem_TaxPercentage === 0 || itemData.menuItem_TaxPercentage
      ? String(itemData.menuItem_TaxPercentage)
      : settings.isTaxEnabled && settings.s_VAT
        ? settings.s_VAT.toString()
        : "",
  menuItem_TakeAwayPrice:
    itemData.menuItem_TakeAwayPrice === 0 || itemData.menuItem_TakeAwayPrice
      ? String(itemData.menuItem_TakeAwayPrice)
      : "",
  menuItem_DeliveryPrice:
    itemData.menuItem_DeliveryPrice === 0 || itemData.menuItem_DeliveryPrice
      ? String(itemData.menuItem_DeliveryPrice)
      : "",
  localizeID: itemData.localizeID || "en",
});

const mapApiItem = (itemData, settings) => {
  const hasVariant = !!(itemData.menuItem_HasVariant || itemData.MenuItem_HasVariant);
  const parentId =
    itemData.menuitem_VariantParentID ||
    itemData.Menuitem_VariantParentID ||
    itemData.parentMenuItemID ||
    itemData.ParentMenuItemID ||
    0;
  const isVariant = !!(
    itemData.menuItem_IsVariant ||
    itemData.MenuItem_IsVariant ||
    parentId > 0 ||
    String(itemData.menuItem_Type || itemData.MenuItem_Type || "").toLowerCase() ===
      "variant"
  );
  const defaultVariantTitle =
    itemData.menuItem_DefaultVariantTitle ||
    itemData.MenuItem_DefaultVariantTitle ||
    "";
  const parentMenuItemName =
    itemData.parentMenuItem_Name ||
    itemData.ParentMenuItem_Name ||
    "";

  return {
    id: itemData.menuItem_ID || itemData.MenuItem_ID,
    rawItem: itemData,
    formState: buildFormState(
      {
        ...itemData,
        menuItem_Name: itemData.menuItem_Name || itemData.MenuItem_Name,
        menuItem_KitchenDisplayName:
          itemData.menuItem_KitchenDisplayName || itemData.MenuItem_KitchenDisplayName,
        menuItem_SalePrice: itemData.menuItem_SalePrice ?? itemData.MenuItem_SalePrice,
        menuItem_CostPrice: itemData.menuItem_CostPrice ?? itemData.MenuItem_CostPrice,
        menuItem_TaxPercentage:
          itemData.menuItem_TaxPercentage ?? itemData.MenuItem_TaxPercentage,
        menuItem_TakeAwayPrice:
          itemData.menuItem_TakeAwayPrice ?? itemData.MenuItem_TakeAwayPrice,
        menuItem_DeliveryPrice:
          itemData.menuItem_DeliveryPrice ?? itemData.MenuItem_DeliveryPrice,
        localizeID: itemData.localizeID || itemData.LocalizeID,
        isTaxExempt: itemData.isTaxExempt ?? itemData.IsTaxExempt,
      },
      settings
    ),
    toggles: {
      isTaxExempt: !!(itemData.isTaxExempt ?? itemData.IsTaxExempt),
      menuItem_HasVariant: hasVariant,
    },
    loading: false,
    error: null,
    hasVariant,
    isVariant: isVariant && !hasVariant,
    parentId: isVariant && !hasVariant ? parentId : 0,
    defaultVariantTitle,
    parentMenuItemName,
  };
};

const BulkEditItems = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData, settingsData } = useSelector((state) => state.auth);
  const selectedItemIds = state?.selectedItemIds || [];
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const [items, setItems] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [compCurrency, setCompCurrency] = useState(profileData?.compCurrency || "GBP");
  const [searchTerm, setSearchTerm] = useState("");
  const [settings, setSettings] = useState({
    s_VAT: settingsData?.s_VAT || 0,
    isTaxEnabled: settingsData?.isTaxEnabled ?? false,
  });

  const hasFetchedRef = useRef(false);

  const themeStyle = {
    "--be-accent": themeColor,
    "--be-accent-soft": `${themeColor}24`,
    "--be-accent-shadow": `${themeColor}40`,
  };

  const getCurrencySymbol = (currencyCode) =>
    currencySymbols[currencyCode] || currencyCode;

  useEffect(() => {
    if (profileData) {
      setCompCurrency(profileData?.compCurrency || "GBP");
    }
  }, [profileData?.compCurrency]);

  useEffect(() => {
    if (settingsData) {
      setSettings({
        s_VAT: settingsData.s_VAT || 0,
        isTaxEnabled: settingsData.isTaxEnabled ?? false,
      });
    }
  }, [settingsData]);

  const fetchItemsData = useCallback(async () => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    if (!CompID || selectedItemIds.length === 0) {
      showErrorToast("Invalid selection or company ID");
      navigate("/items/manageItems");
      return;
    }

    try {
      setGlobalLoading(true);

      const response = await axiosInstance.post(APIs.POST_BULK_GET_ITEMS, {
        compID: CompID,
        menuItemIds: selectedItemIds,
      });

      if (!response.data.status) {
        showErrorToast(response.data.message || "Failed to fetch items");
        navigate("/items/manageItems");
        return;
      }

      const fetchedItemsData = response.data.data?.items || [];
      const failedItemsData = response.data.data?.failedItems || [];
      const processedItems = fetchedItemsData.map((itemData) =>
        mapApiItem(itemData, settings)
      );

      // Attach parent names onto variant rows for "Parent (Variant)" display
      const parentNames = {};
      processedItems.forEach((item) => {
        if (item.hasVariant) {
          parentNames[item.id] = item.formState.menuItem_Name || `Item ${item.id}`;
        }
      });
      processedItems.forEach((item) => {
        if (item.isVariant && item.parentId && !item.parentMenuItemName) {
          item.parentMenuItemName = parentNames[item.parentId] || "";
        }
      });

      setItems(processedItems);

      if (processedItems.length === 0) {
        let message = "No editable items found.";
        if (failedItemsData.length > 0) {
          message += ` ${failedItemsData.length} item(s) had loading errors.`;
        }
        showErrorToast(message);
        navigate("/items/manageItems");
        return;
      }

      const variantCount = processedItems.filter((i) => i.isVariant).length;
      const parentCount = processedItems.filter((i) => i.hasVariant).length;
      if (variantCount > 0 || failedItemsData.length > 0) {
        const parts = [`${processedItems.length} row(s) ready to edit`];
        if (parentCount > 0) parts.push(`${parentCount} parent product(s)`);
        if (variantCount > 0) parts.push(`${variantCount} variant(s)`);
        if (failedItemsData.length > 0) {
          parts.push(`${failedItemsData.length} failed to load`);
        }
        showSuccessToast(parts.join(" · "));
      }
    } catch (err) {
      showErrorToast("Failed to fetch items for editing");
      navigate("/items/manageItems");
    } finally {
      setGlobalLoading(false);
    }
  }, [CompID, selectedItemIds, navigate, settings]);

  useEffect(() => {
    fetchItemsData();
  }, []);

  const handleInputChange = (itemIndex, field) => (e) => {
    const value = e.target.value;
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== itemIndex) return item;

        let newFormState = { ...item.formState };

        if (field === "menuItem_TaxPercentage" || field === "menuItem_SalePrice") {
          if (value === "" || /^\d*\.?\d*$/.test(value)) {
            newFormState[field] = value;
          } else {
            showErrorToast(
              field === "menuItem_SalePrice"
                ? "Sale Price must be a valid number (e.g., 100)"
                : "Tax Percentage must be a valid number (e.g., 20)"
            );
            return item;
          }
        } else {
          newFormState[field] = value;
        }

        // Recalc cost for simple items and variants (not for parent-with-variants)
        if (
          !item.hasVariant &&
          (field === "menuItem_SalePrice" || field === "menuItem_TaxPercentage")
        ) {
          const salePrice = parseFloat(newFormState.menuItem_SalePrice) || 0;
          const taxPercentage = item.toggles.isTaxExempt
            ? 0
            : parseFloat(newFormState.menuItem_TaxPercentage) || 0;
          const taxAmount = (salePrice * taxPercentage) / 100;
          const costPrice = salePrice - taxAmount;
          newFormState.menuItem_CostPrice =
            costPrice >= 0 ? costPrice.toFixed(2) : "0.00";
        }

        return { ...item, formState: newFormState };
      })
    );
  };

  const toPayloadItem = (item) => ({
    menuItem_ID: item.rawItem.menuItem_ID || item.rawItem.MenuItem_ID || item.id,
    compID: CompID,
    menuItem_Name: item.formState.menuItem_Name,
    menuItem_KitchenDisplayName: item.formState.menuItem_KitchenDisplayName,
    menuItem_SalePrice: parseFloat(item.formState.menuItem_SalePrice) || 0,
    menuItem_CostPrice: parseFloat(item.formState.menuItem_CostPrice) || 0,
    menuItem_TaxPercentage: parseFloat(item.formState.menuItem_TaxPercentage) || 0,
    menuItem_TakeAwayPrice: parseFloat(item.formState.menuItem_TakeAwayPrice) || 0,
    menuItem_DeliveryPrice: parseFloat(item.formState.menuItem_DeliveryPrice) || 0,
    localizeID: item.formState.localizeID || "en",
  });

  const handleSaveProductGroup = async (indexes) => {
    const uniqueIndexes = [...new Set(indexes)].filter(
      (i) => i >= 0 && i < items.length
    );
    if (uniqueIndexes.length === 0) return;

    setItems((prev) =>
      prev.map((item, idx) =>
        uniqueIndexes.includes(idx) ? { ...item, loading: true } : item
      )
    );

    try {
      const payloadItems = uniqueIndexes.map((i) => toPayloadItem(items[i]));
      const response = await axiosInstance.put(APIs.PUT_BULK_UPDATE_ITEMS, {
        compID: CompID,
        items: payloadItems,
      });

      if (response.data.status) {
        const { successCount, failedCount } = response.data.data || {};
        if (failedCount === 0) {
          showSuccessToast(
            uniqueIndexes.length === 1
              ? "Product updated successfully!"
              : `Product updated (${successCount} row${successCount === 1 ? "" : "s"}).`
          );
        } else {
          showErrorToast(
            `${successCount || 0} saved, ${failedCount} failed for this product.`
          );
        }
      } else {
        showErrorToast(response.data.message || "Failed to update product");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Error updating product");
    } finally {
      setItems((prev) =>
        prev.map((item, idx) =>
          uniqueIndexes.includes(idx) ? { ...item, loading: false } : item
        )
      );
    }
  };

  const handleSaveAll = async () => {
    if (items.length === 0) return;
    setGlobalLoading(true);

    try {
      const response = await axiosInstance.put(APIs.PUT_BULK_UPDATE_ITEMS, {
        compID: CompID,
        items: items.map(toPayloadItem),
      });

      if (response.data.status) {
        const { successCount, failedCount } = response.data.data || {};
        if (failedCount === 0) {
          showSuccessToast(`All ${successCount} items updated successfully!`);
        } else {
          showSuccessToast(
            `${successCount} items updated, ${failedCount} failed.`
          );
        }
        navigate("/items/manageItems");
      } else {
        showErrorToast(response.data.message || "Failed to update items");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Error updating items");
    } finally {
      setGlobalLoading(false);
    }
  };

  const parentNameById = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      if (item.hasVariant) {
        map[item.id] = item.formState.menuItem_Name || `Item ${item.id}`;
      }
    });
    return map;
  }, [items]);

  const getItemDisplayTitle = (item) => {
    const itemName = item.formState.menuItem_Name || `Item ${item.id}`;
    if (item.hasVariant) {
      // Parent row keeps its own name (DefaultVariantTitle is the short default label only)
      return itemName;
    }
    if (item.isVariant) {
      // Prefer stored "Parent (Variant)" title when present
      const stored = (item.defaultVariantTitle || "").trim();
      if (stored.includes("(") && stored.includes(")")) return stored;
      const parentName =
        item.parentMenuItemName ||
        parentNameById[item.parentId] ||
        "";
      return buildVariantDisplayName(parentName, itemName);
    }
    return itemName;
  };

  const filteredIndexes = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => {
        if (!q) return true;
        const parentLabel = item.isVariant
          ? parentNameById[item.parentId] || ""
          : "";
        return (
          (item.formState.menuItem_Name || "").toLowerCase().includes(q) ||
          (item.formState.menuItem_KitchenDisplayName || "")
            .toLowerCase()
            .includes(q) ||
          parentLabel.toLowerCase().includes(q) ||
          String(item.id).includes(q)
        );
      });
  }, [items, searchTerm, parentNameById]);

  const groupedSections = useMemo(() => {
    const parents = [];
    const standalone = [];
    const variantsByParent = {};

    filteredIndexes.forEach(({ item, index }) => {
      if (item.hasVariant) {
        parents.push({ item, index });
      } else if (item.isVariant && item.parentId) {
        if (!variantsByParent[item.parentId]) {
          variantsByParent[item.parentId] = [];
        }
        variantsByParent[item.parentId].push({ item, index });
      } else {
        standalone.push({ item, index });
      }
    });

    const sections = [];

    parents.forEach(({ item, index }) => {
      sections.push({
        key: `parent-${item.id}`,
        kind: "variant-group",
        parent: { item, index },
        variants: variantsByParent[item.id] || [],
      });
      delete variantsByParent[item.id];
    });

    Object.entries(variantsByParent).forEach(([parentId, variants]) => {
      sections.push({
        key: `orphan-variants-${parentId}`,
        kind: "variant-group",
        parent: null,
        orphanTitle: parentNameById[parentId] || `Parent #${parentId}`,
        variants,
      });
    });

    standalone.forEach(({ item, index }) => {
      sections.push({
        key: `item-${item.id}`,
        kind: "single",
        entry: { item, index },
      });
    });

    return sections;
  }, [filteredIndexes, parentNameById]);

  const variantCount = items.filter((i) => i.isVariant).length;
  const parentCount = items.filter((i) => i.hasVariant).length;

  const renderCellInput = (item, index, field, opts = {}) => (
    <input
      className={`bulk-edit-input${opts.num ? " bulk-edit-input--num" : ""}${
        opts.tax ? " bulk-edit-input--tax" : ""
      }`}
      value={item.formState[field] ?? ""}
      onChange={opts.readOnly ? undefined : handleInputChange(index, field)}
      type={opts.type || "text"}
      placeholder={opts.placeholder || ""}
      readOnly={opts.readOnly}
      disabled={opts.readOnly}
      step={opts.step}
      min={opts.min}
      aria-label={opts.ariaLabel || field}
    />
  );

  const renderProductSaveButton = (indexes, loading) => (
    <button
      type="button"
      className="bulk-edit-btn bulk-edit-btn--primary bulk-edit-btn--product-save"
      onClick={() => handleSaveProductGroup(indexes)}
      disabled={loading || globalLoading}
      title="Save this product"
    >
      {loading ? (
        <>
          <span className="spinner-border spinner-border-sm me-1" role="status" />
          Saving…
        </>
      ) : (
        <>
          <i className="fas fa-save me-1" aria-hidden="true" />
          Save
        </>
      )}
    </button>
  );

  const renderParentStrip = (item, index) => (
    <div className="bulk-edit-parent-strip" key={`parent-strip-${item.id}`}>
      <span className="bulk-edit-parent-strip__tag">Parent</span>
      <label className="bulk-edit-parent-strip__field">
        <span>Item name</span>
        {renderCellInput(item, index, "menuItem_Name", {
          ariaLabel: "Parent item name",
        })}
      </label>
      <label className="bulk-edit-parent-strip__field">
        <span>Kitchen name</span>
        {renderCellInput(item, index, "menuItem_KitchenDisplayName", {
          ariaLabel: "Parent kitchen name",
        })}
      </label>
    </div>
  );

  const renderSheetTable = (rows, { showParentChip = false } = {}) => {
    const currency = getCurrencySymbol(compCurrency);
    return (
      <div className="bulk-edit-sheet-wrap">
        <table className="bulk-edit-sheet">
          <thead>
            <tr>
              <th>{showParentChip ? "Parent / variant" : "Name"}</th>
              <th>Kitchen</th>
              <th>{`Sale (${currency})`}</th>
              <th>{`Takeaway (${currency})`}</th>
              <th>{`Delivery (${currency})`}</th>
              <th>Tax %</th>
              <th>{`Cost (${currency})`}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ item, index }) => {
              const parentName = (
                item.parentMenuItemName ||
                parentNameById[item.parentId] ||
                ""
              ).trim();
              return (
                <tr key={`${item.id}-${index}`}>
                  <td>
                    {showParentChip && item.isVariant ? (
                      <div className="bulk-edit-sheet__name">
                        {parentName ? (
                          <span
                            className="bulk-edit-sheet__parent"
                            title={parentName}
                          >
                            {parentName}
                          </span>
                        ) : null}
                        {renderCellInput(item, index, "menuItem_Name", {
                          placeholder: "Variant",
                          ariaLabel: "Variant name",
                        })}
                      </div>
                    ) : (
                      renderCellInput(item, index, "menuItem_Name", {
                        ariaLabel: "Item name",
                      })
                    )}
                  </td>
                  <td>
                    {renderCellInput(item, index, "menuItem_KitchenDisplayName", {
                      ariaLabel: "Kitchen name",
                    })}
                  </td>
                  <td>
                    {renderCellInput(item, index, "menuItem_SalePrice", {
                      type: "number",
                      num: true,
                      ariaLabel: "Sale price",
                    })}
                  </td>
                  <td>
                    {renderCellInput(item, index, "menuItem_TakeAwayPrice", {
                      type: "number",
                      num: true,
                      ariaLabel: "Takeaway price",
                    })}
                  </td>
                  <td>
                    {renderCellInput(item, index, "menuItem_DeliveryPrice", {
                      type: "number",
                      num: true,
                      ariaLabel: "Delivery price",
                    })}
                  </td>
                  <td>
                    {renderCellInput(item, index, "menuItem_TaxPercentage", {
                      type: "number",
                      num: true,
                      tax: true,
                      step: "0.01",
                      min: "0",
                      placeholder: "0",
                      ariaLabel: "Tax percent",
                    })}
                  </td>
                  <td>
                    {renderCellInput(item, index, "menuItem_CostPrice", {
                      type: "number",
                      num: true,
                      readOnly: true,
                      ariaLabel: "Cost price",
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (globalLoading && items.length === 0) {
    return <GridSectionLoader loading={true} minHeight="70vh" />;
  }

  return (
    <div
      className={`container-fluid px-3 px-md-4 py-3 bulk-edit-page${
        isDarkMode ? " bulk-edit-page--dark" : ""
      }`}
      style={themeStyle}
    >
      <div className="bulk-edit-page-header mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <a href="#/items/manageItems">Manage Items</a>
          </li>
          <li className="breadcrumb-item active">Bulk Edit</li>
        </ol>
        <h1 className="page-header mb-0">
          <i className="fas fa-layer-group me-2" aria-hidden="true" />
          Bulk Edit Items
        </h1>
        <p className="bulk-edit-page-subtitle">
          Spreadsheet-style editor for names and pricing. Save per product or use Save all.
        </p>
      </div>

      <div className="bulk-edit-toolbar">
        <div className="bulk-edit-toolbar__meta">
          <strong>{items.length}</strong> row{items.length === 1 ? "" : "s"}
          {parentCount > 0 ? ` · ${parentCount} parent` : ""}
          {variantCount > 0 ? ` · ${variantCount} variant` : ""}
        </div>
        <div className="bulk-edit-toolbar__search">
          <i className="fa fa-search" aria-hidden="true" />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or ID…"
            aria-label="Search items"
          />
        </div>
        <button
          type="button"
          className="bulk-edit-btn bulk-edit-btn--ghost"
          onClick={() => navigate("/items/manageItems")}
          disabled={globalLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="bulk-edit-btn bulk-edit-btn--primary"
          onClick={handleSaveAll}
          disabled={globalLoading || items.length === 0}
        >
          {globalLoading ? "Saving…" : "Save all"}
        </button>
      </div>

      <GridSectionLoader loading={globalLoading} minHeight="200px">
        {groupedSections.length === 0 ? (
          <div className="bulk-edit-empty">No items match your search.</div>
        ) : (
          groupedSections.map((section) => {
            if (section.kind === "single") {
              const { item, index } = section.entry;
              return (
                <div className="bulk-edit-group" key={section.key}>
                  <div className="bulk-edit-group__head">
                    <h2 className="bulk-edit-group__title">
                      {getItemDisplayTitle(item)}
                    </h2>
                    {item.isVariant ? (
                      <span className="bulk-edit-group__badge">Variant</span>
                    ) : null}
                    <div className="bulk-edit-group__head-actions">
                      <span className="bulk-edit-row__id">ID {item.id}</span>
                      {renderProductSaveButton([index], item.loading)}
                    </div>
                  </div>
                  {renderSheetTable([{ item, index }], {
                    showParentChip: !!item.isVariant,
                  })}
                </div>
              );
            }

            const { parent, variants, orphanTitle } = section;
            const groupTitle = parent
              ? getItemDisplayTitle(parent.item)
              : orphanTitle;
            const groupIndexes = [
              ...(parent ? [parent.index] : []),
              ...variants.map((v) => v.index),
            ];
            const groupLoading = groupIndexes.some((i) => items[i]?.loading);
            const groupId = parent?.item?.id || variants[0]?.item?.id;

            return (
              <div className="bulk-edit-group" key={section.key}>
                <div className="bulk-edit-group__head">
                  <h2 className="bulk-edit-group__title">{groupTitle}</h2>
                  <span className="bulk-edit-group__badge bulk-edit-group__badge--parent">
                    Has variants
                  </span>
                  <span className="bulk-edit-group__badge">
                    {variants.length} variant{variants.length === 1 ? "" : "s"}
                  </span>
                  <div className="bulk-edit-group__head-actions">
                    {groupId ? (
                      <span className="bulk-edit-row__id">ID {groupId}</span>
                    ) : null}
                    {renderProductSaveButton(groupIndexes, groupLoading)}
                  </div>
                </div>

                {parent ? renderParentStrip(parent.item, parent.index) : null}

                {variants.length === 0 ? (
                  <div className="bulk-edit-card-empty">
                    No variants were returned for this product.
                  </div>
                ) : (
                  renderSheetTable(variants, { showParentChip: true })
                )}
              </div>
            );
          })
        )}
      </GridSectionLoader>
    </div>
  );
};

export default BulkEditItems;
